import { DefaultEffects, IPersonaStyles, Icon, Persona, PersonaSize, Stack, Text, TooltipHost, getTheme } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import UserActions from './Actions/UserActions';
import { IAreaSegmentProps, ICrossUserProfile } from '../dataTypes';
import { useCurrentAreaId } from 'shared-state/location/hook';
import IvicosStrings from 'kits/language/stringKit';
import { useOtherUsersStatus } from 'shared-state/identity/hooks';
import { AtWorkLocation, STATUS_COLORS, UserStatus } from 'shared-state/identity/types';
import { Building2, MapPin, House, LucideIcon } from 'lucide-react';
import { IC } from 'components/SVGIcon';
import { BusyStatus } from 'kits/IconKit2';
import { getOutOfOfficePhrase } from 'features/Rooms/RoomList/helpers/oooPhrase';

const AT_WORK_PHRASE_ICONS: Record<Exclude<AtWorkLocation, 'custom'>, LucideIcon> = {
    office: Building2,
    remote: House,
    other_location: MapPin
};

// #2099df - use when user is in a meeting in a public room
// #145f9f - use when user is in a meeting in a private room

function formatCountdown(ms: number, durationMinutes?: number): string {
    if (ms <= 0) return '0:00';
    const remainingSeconds = Math.ceil(ms / 1000);
    const fullSeconds = durationMinutes != null ? durationMinutes * 60 : remainingSeconds;
    // Clamp to full duration when within first second so timer always starts at full value (e.g. 30:00)
    const displaySeconds =
        durationMinutes != null && remainingSeconds >= fullSeconds - 1 ? Math.min(remainingSeconds, fullSeconds) : Math.max(0, remainingSeconds);
    const m = Math.floor(displaySeconds / 60);
    const s = displaySeconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

function formatEndTime(timestamp: number): string {
    const d = new Date(timestamp);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function getStatusPhrase(status: UserStatus): string {
    switch (status) {
        case 'available':
            return IvicosStrings.statusAtWork;
        case 'busy':
            return IvicosStrings.statusDoNotDisturb;
        case 'out-of-office':
            return IvicosStrings.statusOutOfOffice;
        case 'on-the-phone':
            return IvicosStrings.statusOnThePhone;
        default:
            return IvicosStrings.statusAway;
    }
}

const UserRow: React.FC<{ user: ICrossUserProfile }> = ({ user }) => {
    const theme = getTheme();
    const otherUsersStatus = useOtherUsersStatus(user.uid) || user.user_status;
    const [, setOtherUsersTick] = useState(0);

    useEffect(() => {
        if (!otherUsersStatus || !otherUsersStatus.endTime) return;
        const id = setInterval(() => {
            setOtherUsersTick((t) => t + 1);
        }, 1000);
        return () => clearInterval(id);
    }, [otherUsersStatus]);

    const otherUsersStatusPhrase = otherUsersStatus
        ? otherUsersStatus.status === 'busy' && otherUsersStatus.endTime && otherUsersStatus.endTime > Date.now()
            ? IvicosStrings.focusWorkUntilEnd + ' ' + formatEndTime(otherUsersStatus.endTime)
            : otherUsersStatus.status === 'on-the-phone' && otherUsersStatus.endTime && otherUsersStatus.endTime > Date.now()
            ? IvicosStrings.onCallUntilEnd + ' ' + formatEndTime(otherUsersStatus.endTime)
            : otherUsersStatus.status === 'away'
            ? otherUsersStatus.phrase
            : otherUsersStatus.status === 'out-of-office'
            ? getOutOfOfficePhrase(
                  otherUsersStatus.absenceComment ?? '',
                  otherUsersStatus.returnDate ? new Date(otherUsersStatus.returnDate) : undefined,
                  otherUsersStatus.returnTime ?? undefined
              ) ?? getStatusPhrase(otherUsersStatus.status as UserStatus)
            : otherUsersStatus.status === 'available' && otherUsersStatus.phrase
            ? otherUsersStatus.phrase
            : otherUsersStatus.status
            ? getStatusPhrase(otherUsersStatus.status as UserStatus)
            : undefined
        : undefined;

    const customPersonaStyles: Partial<IPersonaStyles> = {
        secondaryText: {
            color: user.isUserAloneInRoom ? undefined : 'red',
            fontWeight: 'bold',
            fontSize: 12
        }
    };

    const isUserInMeetingInPersonalRoom = !user.isUserAloneInRoom && user?.room?.includes('personal-') && !user.user_status;
    const isUserInMeetingInPublicRoom = !user.isUserAloneInRoom && !user?.room?.includes('personal-') && !user.user_status;
    const isOutOfOfficeUser = user.user_status?.status === 'out-of-office';

    return (
        <Stack
            key={user.uid}
            style={{
                width: '100%',
                padding: '0 10px',
                maxHeight: 80,
                height: 'auto',
                backgroundColor: theme.palette.white,
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: `0.5px solid ${theme.palette.black}`
            }}
        >
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
                <Stack horizontal verticalAlign="center" style={{ position: 'relative', height: 65 }}>
                    <Stack
                        tokens={{ childrenGap: 10 }}
                        style={{
                            padding: 3,
                            borderRadius: '50%',
                            background: isUserInMeetingInPersonalRoom
                                ? '#2099df'
                                : isUserInMeetingInPublicRoom
                                ? '#145f9f'
                                : `linear-gradient(135deg, ${STATUS_COLORS[otherUsersStatus?.status as UserStatus]}, ${
                                      STATUS_COLORS[otherUsersStatus?.status as UserStatus]
                                  }dd)`
                        }}
                    >
                        <Stack
                            style={{
                                padding: 2,
                                borderRadius: '50%',
                                background: theme.palette.white
                            }}
                        >
                            <Persona
                                styles={customPersonaStyles}
                                size={PersonaSize.size40}
                                text={user.displayName}
                                imageUrl={user.profileImage}
                                hidePersonaDetails={true}
                                aria-label="user-name-in-user-face"
                            />
                        </Stack>
                    </Stack>
                    {/* Timer badge (busy or on-the-phone): countdown on profile pic */}
                    {(otherUsersStatus?.status === 'busy' || otherUsersStatus?.status === 'on-the-phone') &&
                        otherUsersStatus?.endTime &&
                        otherUsersStatus?.endTime > Date.now() &&
                        user.isUserAloneInRoom && (
                            <Stack
                                horizontal
                                verticalAlign="center"
                                tokens={{ childrenGap: 4 }}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: -12,
                                    zIndex: 10,
                                    padding: '4px',
                                    borderRadius: 20,
                                    backgroundColor: theme.palette.white,
                                    boxShadow: DefaultEffects.elevation4
                                }}
                            >
                                {otherUsersStatus?.status === 'on-the-phone' ? (
                                    <Icon iconName="Phone" style={{ color: STATUS_COLORS['on-the-phone'], fontSize: 14 }} />
                                ) : (
                                    <IC variant="custom" size={12} iconColor={STATUS_COLORS.busy}>
                                        <BusyStatus />
                                    </IC>
                                )}
                                <Text variant="small" style={{ fontWeight: 600, color: theme.palette.black }}>
                                    {formatCountdown(otherUsersStatus?.endTime - Date.now(), otherUsersStatus?.durationMinutes ?? undefined)}
                                </Text>
                            </Stack>
                        )}
                </Stack>
                {otherUsersStatus && (
                    <Stack verticalAlign="center" tokens={{ childrenGap: 2 }} style={{ marginLeft: 8 }}>
                        <Text
                            variant="medium"
                            style={{
                                color: theme.palette.black,
                                // maxWidth: stylesForUserPersona.primaryText.maxWidth,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {user.displayName}
                        </Text>
                        {(user.isUserAloneInRoom || isOutOfOfficeUser) && (
                            <TooltipHost content={otherUsersStatusPhrase ?? ''}>
                                <Stack
                                    horizontal
                                    verticalAlign="center"
                                    tokens={{ childrenGap: 4 }}
                                    style={{
                                        // maxWidth: stylesForUserPersona.primaryText.maxWidth,
                                        overflow: 'hidden'
                                    }}
                                >
                                    {otherUsersStatus?.status === 'available' &&
                                        (() => {
                                            const loc = otherUsersStatus?.location;
                                            if (!loc || loc === 'custom') return null;
                                            const LocationIcon = AT_WORK_PHRASE_ICONS[loc];
                                            return <LocationIcon size={14} color={theme.palette.neutralSecondary} />;
                                        })()}
                                    <Text
                                        variant="small"
                                        style={{
                                            color: theme.palette.neutralSecondary,
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            fontSize: 12,
                                            fontWeight: 500
                                        }}
                                    >
                                        {otherUsersStatusPhrase}
                                    </Text>
                                </Stack>
                            </TooltipHost>
                        )}
                    </Stack>
                )}
            </Stack>

            {/* <Persona
                imageUrl={user.profileImage}
                imageInitials={
                    user.displayName.split(' ').length > 1
                        ? `${user.displayName.split(' ')[0].slice(0, 1).toUpperCase()}${user.displayName.split(' ')[1].slice(0, 1).toUpperCase()}`
                        : `${user.displayName.split(' ')[0].slice(0, 1).toUpperCase()}`
                }
                text={user.displayName}
                secondaryText={user.isUserAloneInRoom ? '' : IvicosStrings.inAMeetingActionTitle}
                onRenderSecondaryText={(props, defaultRender) => {
                    if (!user.isUserAloneInRoom) {
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Icon iconName="Video" style={{ color: 'red', fontSize: 12, marginTop: 2, fontWeight: 'bold' }} />
                                {defaultRender?.(props)}
                            </div>
                        );
                    }
                    return null;
                }}
                size={PersonaSize.size40}
                imageAlt={user.displayName}
                styles={customPersonaStyles}
            /> */}

            <Stack horizontal>
                <UserActions user={user} />
            </Stack>
        </Stack>
    );
};

const AreaSegment: React.FC<IAreaSegmentProps> = ({ areaName, users, iconName, handleToggleArea, expanded, textColor, mobileView }) => {
    const [currentAreaId] = useCurrentAreaId();
    const isUserInsideOFAnArea = currentAreaId && window.location.pathname.includes('/areas/');

    if (!users.length) {
        return null; // Skip rendering the area if there are no users
    }

    return (
        <Stack>
            {isUserInsideOFAnArea ? (
                <>
                    <Stack style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }} onClick={handleToggleArea}>
                        <Icon
                            iconName={iconName}
                            style={{
                                fontSize: '12px',
                                fontWeight: 'bold',
                                color: mobileView ? '#000' : textColor
                            }}
                        />
                        <Text
                            variant="medium"
                            style={{
                                fontWeight: 600,
                                marginLeft: 5,
                                color: mobileView ? '#000' : textColor
                            }}
                        >
                            {areaName === IvicosStrings.crossAreaSearchPersonOutOfOffice ? areaName : `In ${areaName}`}
                        </Text>
                    </Stack>
                    {expanded && (
                        <Stack
                            style={{
                                margin: '10px 0'
                            }}
                            tokens={{
                                childrenGap: 10
                            }}
                        >
                            {users.map((user) => (
                                <UserRow user={user} key={user.uid} />
                            ))}
                        </Stack>
                    )}
                </>
            ) : (
                <Stack
                    tokens={{
                        childrenGap: 10
                    }}
                >
                    {areaName === IvicosStrings.crossAreaSearchPersonOutOfOffice ? (
                        <>
                            <Stack style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }} onClick={handleToggleArea}>
                                <Icon
                                    iconName={iconName}
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: mobileView ? '#000' : textColor
                                    }}
                                />
                                <Text
                                    variant="medium"
                                    style={{
                                        fontWeight: 600,
                                        marginLeft: 5,
                                        color: mobileView ? '#000' : textColor
                                    }}
                                >
                                    {areaName}
                                </Text>
                            </Stack>
                            {expanded && (
                                <Stack
                                    style={{
                                        margin: '10px 0'
                                    }}
                                    tokens={{
                                        childrenGap: 10
                                    }}
                                >
                                    {users.map((user) => (
                                        <UserRow user={user} key={user.uid} />
                                    ))}
                                </Stack>
                            )}
                        </>
                    ) : (
                        users.map((user) => <UserRow user={user} key={user.uid} />)
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default AreaSegment;
